import { FRACTIONAL_DIGITS, TOKENS_MAX, MIN_INTEGER_DIGITS, MAX_INTEGER_DIGITS } from '~/utils/constants';

// Decimal pattern for input validation - allow empty string or valid decimal numbers
const DECIMAL_REGEX = new RegExp(
  `^(\\d{${MIN_INTEGER_DIGITS},${MAX_INTEGER_DIGITS}}(\\.\\d{0,${FRACTIONAL_DIGITS}})?|\\.\\d{1,${FRACTIONAL_DIGITS}})?$`
);

const normalizeNumberString = (rawValue: string): string => {
  if (!rawValue) {
    return '';
  }

  // Keep only digits, dots, and commas, then convert commas to dots
  let value = rawValue.replace(/[^\d.,]/g, '').replace(/,/g, '.');

  // Collapse multiple dots into a single one (keep the first)
  const firstDotIndex = value.indexOf('.');
  if (firstDotIndex !== -1) {
    value = value.slice(0, firstDotIndex + 1) + value.slice(firstDotIndex + 1).replace(/\./g, '');
  }

  // Split into integer and fractional parts
  const hasDot = firstDotIndex !== -1;
  let [integerPart, fractionalPart = ''] = value.split('.');

  // Remove leading zeros from integer part, but keep single '0' if needed
  if (integerPart) {
    integerPart = integerPart.replace(/^0+(?=\d)/, '');
  }
  // Only set to '0' if we have a fractional part or the value is completely empty
  if (!integerPart && (fractionalPart || !hasDot)) {
    integerPart = '0';
  }

  // Limit fractional part to 6 digits
  if (fractionalPart) {
    fractionalPart = fractionalPart.slice(0, FRACTIONAL_DIGITS);
  }

  // Rebuild value
  const normalized = hasDot ? `${integerPart}.${fractionalPart}` : integerPart;
  return normalized;
};

export const handleInputChange = (value: string): string => {
  const normalized = normalizeNumberString(value);

  // Check if the value exceeds TOKENS_MAX
  const numericValue = parseFloat(normalized);
  if (!isNaN(numericValue) && numericValue > TOKENS_MAX) {
    return TOKENS_MAX.toString();
  }

  return normalized;
};

export const keydownHandler = (evt: Event | KeyboardEvent, onValidKey?: () => void): void => {
  if (!(evt instanceof KeyboardEvent)) return;

  const event = evt as KeyboardEvent;

  const prevent = () => {
    event.preventDefault();
    event.stopPropagation();
  };

  const allowedNavigationKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Control', 'Meta'];

  // Shortcuts: allow select all, copy, cut; explicitly forbid paste (Ctrl/Cmd+V)
  const isSelectCopyCut = (event.ctrlKey || event.metaKey) && ['KeyA', 'KeyC', 'KeyX'].includes(event.code);
  const isPaste = (event.ctrlKey || event.metaKey) && event.code === 'KeyV';
  const isShortcut = isSelectCopyCut;

  const isDigitKey = /^[0-9]$/.test(event.key);
  const isTopRowDigitByCode = /^Digit[0-9]$/.test(event.code);
  const isNumpadDigit = /^Numpad[0-9]$/.test(event.code);
  const isConsistentDigit = isTopRowDigitByCode && isDigitKey && !event.shiftKey;
  const isDotKey = event.code === 'Period' || event.code === 'NumpadDecimal' || event.key === '.' || event.key === ',';

  const isLegacyBackquote = (event as any).keyCode === 192 || (event as any).which === 192;
  const isAltModifiedNonArrow = event.altKey && event.key !== 'ArrowLeft' && event.key !== 'ArrowRight';
  const isBackquote = event.code === 'Backquote' || event.key === '`';
  const isDeadKey = event.key === 'Dead';
  const isInvalidSingleChar = event.key.length === 1 && !isDigitKey && event.key !== '.';

  // 1) Immediate blocks
  if (isLegacyBackquote || isAltModifiedNonArrow || isBackquote || isDeadKey) {
    prevent();
    return;
  }
  if (isPaste) {
    prevent();
    return;
  }

  // Block pure Cmd/Ctrl key presses without combinations
  if ((event.metaKey || event.ctrlKey) && !isSelectCopyCut) {
    prevent();
    return;
  }

  const input = event.target as HTMLInputElement;
  const rawValue = input.value;
  const value = rawValue.replace(/\s/g, '');
  const rawStart = input.selectionStart ?? rawValue.length;
  const rawEnd = input.selectionEnd ?? rawValue.length;
  const start = countNonSpaceUpToIndex(rawValue, rawStart);
  const end = countNonSpaceUpToIndex(rawValue, rawEnd);

  // 2) Dot/Comma handling (normalize and limit)
  if (isDotKey) {
    prevent();

    const selectedText = value.slice(start, end);
    const hasDotAlready = value.includes('.');
    const selectionContainsDot = selectedText.includes('.');
    const wouldAddSecondDot = hasDotAlready && !selectionContainsDot;

    const insertingAtStart = start === 0;
    // Always use dot as decimal separator, convert comma to dot
    const decimalSeparator = '.';
    const nextValueWithDot =
      insertingAtStart && !hasDotAlready
        ? '0' + decimalSeparator + value.slice(end)
        : value.slice(0, start) + decimalSeparator + value.slice(end);

    if (wouldAddSecondDot || !DECIMAL_REGEX.test(nextValueWithDot)) return;

    input.value = formatInputWithSpaces(nextValueWithDot);
    input.dispatchEvent(new InputEvent('input', { bubbles: true }));

    // Call callback for valid dot key
    onValidKey?.();
    return;
  }

  // 3) Block invalid single-char (non-shortcut)
  if (isInvalidSingleChar && !isShortcut) {
    prevent();
    return;
  }

  // 4) Allow only digits, navigation, or shortcuts
  const allowedByType =
    isDigitKey || allowedNavigationKeys.includes(event.key) || isConsistentDigit || isNumpadDigit || isShortcut;
  if (!allowedByType) {
    prevent();
    return;
  }

  // 5) Fractional precision guard for digit input
  if (isDigitKey || isConsistentDigit || isNumpadDigit) {
    const dotIndex = value.indexOf('.');
    const isCaretOnly = start === end;
    const isInFractionalPart = dotIndex !== -1 && start > dotIndex;
    const fractionalLength = dotIndex === -1 ? 0 : value.length - (dotIndex + 1);

    const exceedsMaxFractionalAtCaret = isCaretOnly && isInFractionalPart && fractionalLength >= FRACTIONAL_DIGITS;
    const proposed = value.slice(0, start) + event.key + value.slice(end);
    const fitsDecimals = DECIMAL_REGEX.test(proposed);

    // Check if proposed value exceeds TOKENS_MAX
    const proposedNumeric = parseFloat(proposed);
    const exceedsMaxTokens = !isNaN(proposedNumeric) && proposedNumeric > TOKENS_MAX;

    if (exceedsMaxFractionalAtCaret || !fitsDecimals || exceedsMaxTokens) {
      prevent();
      return;
    }
  }

  // Call callback for valid keys (but not for shortcuts)
  if (!isShortcut) {
    onValidKey?.();
  }
};

// Helpers and visual formatter/parser

// Count non-space characters up to the given raw index (maps caret from raw string to space-stripped string)
const countNonSpaceUpToIndex = (raw: string, rawIndex: number): number => {
  let count = 0;
  for (let i = 0; i < Math.min(raw.length, rawIndex); i++) {
    if (raw[i] !== ' ') count++;
  }
  return count;
};

export const formatInputWithSpaces = (raw: string): string => {
  if (!raw) return '';

  const thousandsSeparatorPattern = /^\d{1,3}(,\d{3})*(\.\d+)?$/;
  const isThousandsFormat = thousandsSeparatorPattern.test(raw);

  let cleaned: string;
  if (isThousandsFormat) {
    cleaned = raw.replace(/,/g, '');
  } else {
    const commaCount = (raw.match(/,/g) || []).length;
    const dotCount = (raw.match(/\./g) || []).length;

    if (commaCount > 1) {
      cleaned = raw.replace(/,/g, '');
    } else if (commaCount === 1 && dotCount === 0) {
      const commaIndex = raw.indexOf(',');
      const afterComma = raw.slice(commaIndex + 1);
      if (/^\d{3}$/.test(afterComma)) {
        cleaned = raw.replace(/,/g, '');
      } else {
        cleaned = raw.replace(/,/g, '.').replace(/[^\d.]/g, '');
      }
    } else {
      cleaned = raw.replace(/,/g, '.').replace(/[^\d.]/g, '');
    }
  }

  const endsWithDot = cleaned.endsWith('.');
  const parts = cleaned.split('.');
  const intPartRaw = parts[0] ?? '';
  const fracPart = parts[1] ?? '';
  const intPart = intPartRaw.replace(/^0+(?=\d)/, '') || '0';
  const intWithSpaces = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

  if (parts.length > 1) {
    if (endsWithDot) {
      return `${intWithSpaces}.`;
    }
    return fracPart !== '' ? `${intWithSpaces}.${fracPart}` : intWithSpaces;
  }

  return intWithSpaces;
};

export const parseInputSpaces = (raw: string): string => {
  if (!raw) return '';
  return raw.replace(/\s/g, '');
};
